import { memo, useCallback, useMemo, useRef, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { ContactPreview } from 'clickNgo/entities/contact/ui'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SimpleTable, usePagination } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'
import { Nullable } from 'cng-common/models/utils'
import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'

import { contactReassignTranslations } from '../config/translation'

import styles from './ContactReassignContent.module.scss'

type Props = {
  reassigningWhat: string
  availableContacts: IContact[]
  errorMessage?: string
  onClose: () => void
  onContactSelect: (contact: IContact) => void
}

export const ContactReassignContent = memo<Props>(
  ({ availableContacts, onContactSelect, onClose, reassigningWhat = 'this', errorMessage }) => {
    const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
    const [previewContact, setPreviewContact] = useState<IContact | null>(null)
    const [isContactViewVisible, setIsContactViewVisible] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const pageSize = useRef(5)

    const filteredContacts = useMemo(() => {
      return availableContacts.filter((contact) => {
        return (
          contact.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.contactId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.hpRoles.some((role) => role.toLowerCase().includes(searchQuery.toLowerCase())) ||
          contact.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })
    }, [availableContacts, searchQuery])

    const { onPageChange, page, paginatedList, total } = usePagination(
      filteredContacts,
      pageSize.current
    )

    const handleContactSelect = useCallback(() => {
      if (selectedContact) {
        onContactSelect(selectedContact)
      }
    }, [selectedContact, onContactSelect])

    const handleClose = useCallback(() => {
      setSelectedContact(null)
      onClose()
    }, [onClose])

    const handleContactViewToggle = useCallback((contact: Nullable<IContact>) => {
      setIsContactViewVisible((prev) => !prev)
      setPreviewContact(contact)
    }, [])

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <h5>
              <Translation text={contactReassignTranslations.title} />
            </h5>
            <p className={styles.subtitle}>
              <Translation
                text={contactReassignTranslations.description}
                replaceParams={[
                  {
                    key: '{what}',
                    value: reassigningWhat,
                  },
                ]}
              />
            </p>
          </div>
          <div className={styles.buttonContainer}>
            <div>
              <Button onClick={handleClose} color="danger">
                <Translation text={contactReassignTranslations.button.cancel} />
              </Button>
            </div>
            <div>
              <Button onClick={handleContactSelect} disabled={!selectedContact}>
                <Translation text={contactReassignTranslations.button.confirm} />
              </Button>
            </div>
          </div>
        </div>
        <RenderWhen condition={!!errorMessage}>
          <InfoBlock type={InfoBlockType.Danger} text={errorMessage} hasIcon hasCloseButton />
        </RenderWhen>
        <div className={styles.searchContainer}>
          <Input
            label={<Translation text={contactReassignTranslations.input.search} />}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>
        <div className={styles.tableContainer}>
          <SimpleTable className={styles.table}>
            <SimpleTable.HeaderRow>
              <SimpleTable.HeaderCell>
                <Translation text={contactReassignTranslations.table.header.contactId} />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell>
                <Translation text={contactReassignTranslations.table.header.name} />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell>
                <Translation text={contactReassignTranslations.table.header.email} />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell>
                <Translation text={contactReassignTranslations.table.header.hpRoles} />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell style={{ width: '62px' }} />
            </SimpleTable.HeaderRow>
            {paginatedList.map((contact) => (
              <SimpleTable.BodyRow
                key={contact.contactId}
                onClick={() => setSelectedContact(contact)}
                data-selected={selectedContact?.contactId === contact.contactId}
              >
                <SimpleTable.BodyCell>{contact.contactId}</SimpleTable.BodyCell>
                <SimpleTable.BodyCell>{`${contact.firstName} ${contact.lastName}`}</SimpleTable.BodyCell>
                <SimpleTable.BodyCell>{contact.email}</SimpleTable.BodyCell>
                <SimpleTable.BodyCell>
                  <Tooltip
                    tipContent={() => (
                      <div>{contact.hpRoles.join(', ') || 'No roles assigned'}</div>
                    )}
                  >
                    <div className={styles.hpRoles}>{contact.hpRoles.join(', ') || '-'}</div>
                  </Tooltip>
                </SimpleTable.BodyCell>
                <SimpleTable.BodyCell>
                  <Tooltip
                    tipContent={() => (
                      <Translation text={contactReassignTranslations.tooltip.view} />
                    )}
                  >
                    <Button
                      className={styles.viewButton}
                      onClick={() => {
                        handleContactViewToggle(contact)
                      }}
                      iconButton="kbase kbase-eye"
                      color="white"
                    />
                  </Tooltip>
                </SimpleTable.BodyCell>
              </SimpleTable.BodyRow>
            ))}
          </SimpleTable>
        </div>
        <div className={styles.paginationContainer}>
          <SimpleTable.Pagination
            page={page}
            onPageChange={onPageChange}
            total={total}
            pageSize={pageSize.current}
            classes={{ container: styles.pagination, button: styles.paginationButton }}
          />
        </div>
        <ContactPreview
          isVisible={isContactViewVisible}
          contact={previewContact!}
          onClose={() => {
            handleContactViewToggle(null)
          }}
        />
      </div>
    )
  }
)

ContactReassignContent.displayName = 'ContactReassignContent'
